import { Suspense } from "react"
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router"
import { z } from "zod"
import { Skeleton } from "toptaal-shared"
import { checkPermissions } from "@/utils/checkPermissions"
import { ReportForm } from "@/components/forms/report/ReportForm"
import { reportVariant } from "@/api/types/reports"
import {
  endB1RegularType,
  progressReport200HoursType,
  progressReport400HoursType,
  scaleDownA2Type,
} from "@/api/types/projects"
import { ReportFormInput } from "@/schemas/report/reportSchema"
import { mapDuoExamResultsToPrefill } from "@/schemas/report/mapDuoExamResultsToPrefill"
import { mapIntakeResultToTrajectoryFormPrefill } from "@/schemas/report/mapIntakeResultToTrajectoryFormPrefill"
import { mapStudentToScalingDeadlinePrefill } from "@/schemas/report/mapStudentToScalingDeadlinePrefill"
import { useLatestStudentDuoExamResultsSuspenseQuery } from "@/api/queries/students/useLatestStudentDuoExamResultsSuspenseQuery"
import { useIntakeResultSuspenseQuery } from "@/api/queries/students/useIntakeResultSuspenseQuery"
import { useStudentSuspenseQuery } from "@/api/queries/students/useStudentSuspenseQuery"
import { useReportSubmissionSuspenseQuery } from "@/api/queries/reports/useReportSubmissionSuspenseQuery"

// No backend GET endpoint exists yet for a report instance (writes-only
// API), so `variant` can't be fetched here -- it travels as a search param
// set by whoever navigates in (currently only
// StudentProgressReportMissingDataTodo.tsx, from the todo's own metadata).
export const Route = createFileRoute(
  "/beheer/cursisten/$studentId/rapportages/$reportId/",
)({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "students"),
  validateSearch: z.object({
    variant: z.enum(reportVariant),
    role: z.enum(["teacher", "planner"]).optional(),
  }),
})

function ReportFormSkeleton() {
  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <Skeleton className="h-8 w-1/2 mb-4" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-5/6 mb-6" />
      <Skeleton className="h-20 w-full mb-6" />
      <Skeleton className="h-10 w-32" />
    </div>
  )
}

/** end_b1_regular only -- prefills the exam-results composite from
 * GET /students/:id/duo_exam_results/latest before the form mounts. */
function EndB1RegularReportForm({
  studentId,
  reportId,
}: {
  studentId: number
  reportId: number
}) {
  const { data } = useLatestStudentDuoExamResultsSuspenseQuery(studentId)
  const { data: intakeResults } = useIntakeResultSuspenseQuery(studentId)
  const existingValues: Partial<ReportFormInput> = {
    ...mapIntakeResultToTrajectoryFormPrefill(intakeResults.intakeResults),
    ...mapDuoExamResultsToPrefill(data.data.results),
  }

  return (
    <ReportForm
      studentId={studentId}
      reportId={reportId}
      variant={endB1RegularType}
      existingValues={existingValues}
    />
  )
}

function ProgressReportFormWithPrefill({
  studentId,
  reportId,
  variant,
  role,
}: {
  studentId: number
  reportId: number
  variant: typeof progressReport200HoursType | typeof progressReport400HoursType
  role?: "teacher" | "planner"
}) {
  const { data } = useReportSubmissionSuspenseQuery(reportId)
  const { data: intakeResults } = useIntakeResultSuspenseQuery(studentId)
  const existingValues = {
    ...mapIntakeResultToTrajectoryFormPrefill(intakeResults.intakeResults),
    ...data.teacher?.fields,
    ...data.planner?.fields,
  } as Partial<ReportFormInput>

  return (
    <ReportForm
      studentId={studentId}
      reportId={reportId}
      variant={variant}
      existingValues={existingValues}
      role={role}
    />
  )
}

function ScaleDownA2ReportForm({
  studentId,
  reportId,
}: {
  studentId: number
  reportId: number
}) {
  const { data: student } = useStudentSuspenseQuery(studentId)
  const existingValues = mapStudentToScalingDeadlinePrefill(student.data)

  return (
    <ReportForm
      studentId={studentId}
      reportId={reportId}
      variant={scaleDownA2Type}
      existingValues={existingValues}
    />
  )
}

function RouteComponent() {
  const { studentId, reportId } = useParams({
    from: "/beheer/cursisten/$studentId/rapportages/$reportId/",
  })
  const { variant, role } = useSearch({
    from: "/beheer/cursisten/$studentId/rapportages/$reportId/",
  })

  if (variant === endB1RegularType) {
    return (
      <Suspense fallback={<ReportFormSkeleton />}>
        <EndB1RegularReportForm
          studentId={Number(studentId)}
          reportId={Number(reportId)}
        />
      </Suspense>
    )
  }

  if (
    variant === progressReport200HoursType ||
    variant === progressReport400HoursType
  ) {
    return (
      <Suspense fallback={<ReportFormSkeleton />}>
        <ProgressReportFormWithPrefill
          studentId={Number(studentId)}
          reportId={Number(reportId)}
          variant={variant}
          role={role}
        />
      </Suspense>
    )
  }

  if (variant === scaleDownA2Type) {
    return (
      <Suspense fallback={<ReportFormSkeleton />}>
        <ScaleDownA2ReportForm
          studentId={Number(studentId)}
          reportId={Number(reportId)}
        />
      </Suspense>
    )
  }

  return (
    <ReportForm
      studentId={Number(studentId)}
      reportId={Number(reportId)}
      variant={variant}
      existingValues={null}
    />
  )
}
